import * as React from 'react';
import type { CalendarEvent } from '../../../MedicareProAPI/Entities/CalendarEvent';
import { Utilities } from '../../../MedicareProAPI/Utilities/Utilities';
import { DAYS } from '../../../MedicareProAPI/Utilities/Languages';
import type { User } from '../../../MedicareProAPI/Entities/User/User';
import type { CalendarWeekDay } from './CalendarWeek';
import { UserAvatar } from '../User/UserAvatar';

export interface CalendarWeekGroupProps {
  events?: CalendarEvent[];
  users?: User[];
  dateTime?: Date;
  onEdit?: (calendarEvent: CalendarEvent) => void;
  showWeekend?: boolean;
}

export const CalendarWeekGroup = (props: CalendarWeekGroupProps): React.JSX.Element => {
  const [daysOfWeek, setDaysOfWeek] = React.useState<CalendarWeekDay[]>();
  const [dateTime, setDateTime] = React.useState<Date>();
  const [events, setEvents] = React.useState<CalendarEvent[][]>();
  const [users, setUsers] = React.useState<User[]>();
  const [allDayEvents, setAllDayEvents] = React.useState<CalendarEvent[][]>();
  const [showWeekend, setShowWeekend] = React.useState<boolean>(true);

  React.useEffect(() => {
    if (props.dateTime) {
      setDateTime(props.dateTime);
      const daysList: CalendarWeekDay[] = [];
      const currentDayOfWeek = Utilities.getFirstDayOfWeek(props.dateTime);

      DAYS.forEach((daySingle, dayIndex) => {
        const singleDay: CalendarWeekDay = {
          date: currentDayOfWeek.getDate(),
          label: daySingle,
        };

        daysList.push(singleDay);

        currentDayOfWeek.setDate(currentDayOfWeek.getDate() + 1);
      });

      setDaysOfWeek(daysList);
    }
  }, [props.dateTime]);

  React.useEffect(() => {
    setUsers(props.users);
  }, [props.users]);

  React.useEffect(() => {
    setShowWeekend(props.showWeekend);
  }, [props.showWeekend]);

  React.useEffect(() => {
    if (props.events) {
      const events: CalendarEvent[][] = [[], [], [], [], [], [], []];
      const allDayEvents: CalendarEvent[][] = [[], [], [], [], [], [], []];

      props.events.forEach((event) => {
        const startDate = event.getDateTimeStart();

        if (event.isAllDayEvent()) {
          allDayEvents[startDate.getDay()].push(event);
        } else {
          events[startDate.getDay()].push(event);
        }
      });

      setAllDayEvents(allDayEvents);
      setEvents(events);
    }
  }, [props.events]);

  const containerClassNames = [
    'calendar-week-container',
    'calendar-week-grouped-container',
    showWeekend ? 'show-weekend' : 'hide-weekend',
  ];

  return (
    <div className={containerClassNames.join(' ')}>
      <div className="week-names">
        <div className="day"></div>
        {daysOfWeek &&
          daysOfWeek.map((day, dayIndex) => (
            <div key={dayIndex} className={`day text-center day-${dayIndex}`}>
              <div className="pt-2 small">{day.label}</div>
              <div className="pb-2">{day.date}</div>
              {allDayEvents && allDayEvents[dayIndex] && (
                <div className="events">
                  {allDayEvents[dayIndex].map((eventSingle: CalendarEvent) => {
                    return (
                      <div
                        className="badge badge-sm border badge-primary border-primary text-left cp"
                        onClick={(e) => props.onEdit(eventSingle)}
                        key={eventSingle.getId()}
                      >
                        {eventSingle.getName()}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
      </div>

      {users &&
        events &&
        users.map((user) => (
          <div className="week-events" style={{ minHeight: '100px' }}>
            <div className="day">
              <UserAvatar user={user} className="ml-2 mt-2 mb-3" />
            </div>
            {DAYS.map((day, dayIndex) => {
              const userEvents =
                events && events[dayIndex]
                  ? events[dayIndex].filter(
                      (eventSingle: CalendarEvent) =>
                        eventSingle.getUser().getUserId() === user.getUserId()
                    )
                  : null;

              return (
                <div className={`day day-${dayIndex}`}>
                  {userEvents &&
                    userEvents.map((eventSingle: CalendarEvent) => {
                      const dateTimeStart = eventSingle.getDateTimeStart();
                      const dateTimeEnd = eventSingle.getDateTimeEnd();

                      return (
                        <div
                          className="badge badge-sm border badge-primary border-primary text-left cp"
                          onClick={(e) => props.onEdit(eventSingle)}
                          key={eventSingle.getId()}
                        >
                          <span className="font-weight-normal">
                            {eventSingle.isAllDayEvent()
                              ? 'All day'
                              : Utilities.dateTimeTo12HourTime(eventSingle.getDateTimeStart())}
                          </span>{' '}
                          {eventSingle.getName()}
                          {eventSingle.getInds() && eventSingle.getInds().length > 0 && (
                            <>
                              <br />
                              <em className="font-weight-normal">
                                <>with </>
                                {eventSingle.getInds().map((ind, index) => {
                                  return (
                                    ind.getIndName() +
                                    (eventSingle.getInds().length > 1 &&
                                    index + 1 !== eventSingle.getInds().length
                                      ? ', '
                                      : '')
                                  );
                                })}
                              </em>
                            </>
                          )}
                        </div>
                      );
                    })}
                </div>
              );
            })}
          </div>
        ))}
    </div>
  );
};
